import axios from "@axios";
import store from "..";
import { debounce } from "lodash";
import { encryptData } from "@/utils/encryptUserDataUtils";

const DEFAULT_PREFERENCES = {
  lang: "en",
  show_alarm_notifications: true,
  theme: null,
};

const SERVER_REFRESHED_FIELDS = ["allowed_tabs"];

const DEFAULT_GRID_CONFIG = {
  activeTab: null,
  tabs: {},
  sideBar: null,
  sideBarWidth: 580,
  sideBarRight: null,
  sideBarRightWidth: 580,
  bottomBar: null,
};

const cleanObject = (source, validObject) => {
  return Object.keys(validObject).reduce((acc, key) => {
    if (key in source) acc[key] = source[key];
    return acc;
  }, {});
};

const handleFileDownload = (responseData, headers) => {
  const url = window.URL.createObjectURL(
    new Blob([responseData], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })
  );

  const filename = headers["content-disposition"].split("filename=")[1];
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();

  const notification = {
    id: filename,
    progress: 100,
    completed: true,
    view: "users",
    url: url,
    download: filename,
    title: "Reporte de usuarios",
    body: "Descarga completa",
    error: false,
    timestamp: new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString(),
  };

  store.dispatch("grid/updateDownloadNotification", notification);
};

const updateLocalStorageUserData = (userData) => {
  const encryptedUserData = encryptData(userData);
  localStorage.setItem("userData", encryptedUserData);
};

const ensureVideoWallProperty = (userData) => {
  if (userData.video_wall === undefined) {
    userData.video_wall = 0;
    updateLocalStorageUserData(userData);
  }
};

const isEmailFormat = (input) => {
  return typeof input === "string" && input.includes("@") && /^[^@]*@\d+$/.test(input);
};

export default {
  namespaced: true,
  state: {
    items: [],
    user: {},
    usersWithOutGroup: [],
    changeTranslate: 1,
  },
  getters: {
    getChangeTranslate(state) {
      return state.changeTranslate;
    },
    getItems(state) {
      return state.items;
    },
    getSelectedItems(state) {
      return state.items.map((user) => ({
        value: user.id,
        label: user.email,
      }));
    },
    getUser(state) {
      let userLocalStorage = JSON.parse(localStorage.getItem("userData"));
      if (userLocalStorage.video_wall == undefined) {
        userLocalStorage["video_wall"] = 0;

        const encryptetUserData = encryptData(userLocalStorage);
        localStorage.setItem("userData", encryptetUserData);
      }
      return state.user;
    },
    getUsersWithOutGroupSelectItems(state) {
      return state.usersWithOutGroup.map((user) => ({
        value: user.id,
        label: user.email,
      }));
    },
    getPreferences(state) {
      return state.user.config ? state.user.config.preferences : {};
    },
  },
  mutations: {
    setData(state, value) {
      state.items = value;
    },
    setUserData(state, value) {
      state.user = value;
    },

    setServerRefreshedFields(state, value) {
      state.user = { ...state.user, ...value };
    },
    setUsersWithOutGroup(state, value) {
      state.usersWithOutGroup = value;
    },
    updateUserConfig(state, value) {
      state.changeTranslate++;
      const userConfig = state.user.config;

      if (userConfig && "preferences" in value) {
        const validPreferences = cleanObject(value.preferences, DEFAULT_PREFERENCES);
        const userPreferences = cleanObject(userConfig.preferences, DEFAULT_PREFERENCES);
        userConfig.preferences = {};
        const mergedPreferences = { ...userPreferences, ...validPreferences };
        value.preferences = mergedPreferences;
      }

      state.user.config = Object.assign(state.user.config || {}, { ...value });
    },
    setUserImage(state, value) {
      state.user.image = value;
    },
  },
  actions: {
    async getAll({ commit }, params) {
      const response = await axios.get("v1/users", {
        params,
      });

      if (!response.data) {
        return [];
      }

      if (params?.action == "export") {
        function getFilenameFromS3Url(u) {
          const url = new URL(u);
          // si termina en '/', toma el penúltimo
          const parts = url.pathname.split("/");
          let last = parts.pop();
          if (!last) last = parts.pop();
          return decodeURIComponent(last);
        }

        const link = document.createElement("a");
        link.href = response.data;
        link.target = "_blank";
        document.body.appendChild(link);

        link.setAttribute("download", getFilenameFromS3Url(response.data));

        link.click();
        link.remove();
        const notification = {
          id: getFilenameFromS3Url(response.data),
          progress: 100,
          completed: true,
          view: "users",
          url: response.data,
          download: getFilenameFromS3Url(response.data),
          title: "Reporte de usuarios",
          body: "Descarga completa",
          error: false,
          timestamp:
            new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString(),
        };
        store.dispatch("grid/updateDownloadNotification", notification);
      } else {
        commit("setData", response.data.data);
        return response.data;
      }
    },
    async getAndSetAllUsersWithoutGroup({ commit }) {
      const response = await axios.get("v1/users", {
        params: { withGroup: false, withPermissions: false },
        responseType: "json",
      });

      if (!response.data) {
        return [];
      }

      commit("setUsersWithOutGroup", response.data.data);
      return response.data;
    },
    async add(_, params) {
      const { data } = await axios.post("v1/users", params);

      return data;
    },
    async edit(_, { id, params }) {
      const { data } = await axios.put(`v1/users/${id}`, params);

      return data;
    },
    async delete(_, id) {
      const { data } = await axios.delete(`v1/users/${id}`);

      return data;
    },
    async getUserName(_, id) {
      const { data } = await axios.get(`v1/users/get-user-name-email/${id}`);
      return data;
    },
    async assignCameras(_, params) {
      const { data } = await axios.post("v1/user/assign_cameras", params);
      return data;
    },
    setUserDataFromLocalStorage({ dispatch }) {
      const userData = JSON.parse(localStorage.getItem("userData"));
      if (userData) {
        dispatch("setUserData", userData);
      }
    },
    setUserData({ commit, dispatch }, data) {
      commit("setUserData", data);
      dispatch("setUserConfig", data);
    },
    setUserConfig({ commit, dispatch }, data) {
      const hasPreferences = data.config?.preferences;
      const configToUpdate = hasPreferences
        ? data.config
        : { ...(data.config || {}), preferences: { ...DEFAULT_PREFERENCES } };

      dispatch("updateUserConfig", configToUpdate);

      const gridConfig = data.config?.grid || DEFAULT_GRID_CONFIG;
      this.dispatch("grid/initializeStore", gridConfig);
    },
    updateUserConfig: debounce(async function ({ commit, state }, data) {
      commit("updateUserConfig", data);

      let serverUser = null;
      try {
        const response = await axios.put("v1/users/update-config", {
          config: state.user.config,
        });
        serverUser = response?.data?.data ?? null;
      } catch (error) {
        console.error("user/updateUserConfig", error);
      }

      const userData = JSON.parse(localStorage.getItem("userData"));
      userData.config = state.user.config;

      if (serverUser) {
        const refreshed = {};
        SERVER_REFRESHED_FIELDS.forEach((field) => {
          if (serverUser[field] !== undefined) refreshed[field] = serverUser[field];
        });

        if (Object.keys(refreshed).length) {
          Object.assign(userData, refreshed);
          commit("setServerRefreshedFields", refreshed);
        }
      }

      const encryptetUserData = encryptData(userData);
      localStorage.setItem("userData", encryptetUserData);
    }, 1500),
    async updateUserFrameColorAndCheckAlarms(_, { params }) {
      const { data } = await axios.put("v1/users/update-checkalarms-framecolor", params);
      return data;
    },
    async updateUserPassword(_, { params }) {
      const { data } = await axios.put("v1/users/changePassword", params);
      return data;
    },
    async saveUserImage({ commit }, { formData }) {
      const { data } = await axios.post("v1/users/image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      commit("setUserImage", data.data);
      return data;
    },
    async resendEmailVerication(_, id) {
      const { data } = await axios.get(`v1/user/resendSetPassword/${id}`);
      return data;
    },
    async sendPasswordResetEmail(_, email) {
      const { data } = await axios.post(`v1/user/reset-password/send-email`, email);
      return data;
    },
    async generateLink(_, userData) {
      const requestBody = isEmailFormat(userData)
        ? { email: userData }
        : { username: userData };
      const { data } = await axios.post(
        `v1/user/reset-password/generate-link`,
        requestBody
      );
      return data;
    },
    async requestLinkemail(_, email) {
      const { data } = await axios.get(`v1/user/reset-password/request-email/${email}`);
      return data;
    },
    clear({ commit }) {
      commit("setData", []);
    },
  },
};
